/** The Nodes tab: every node type, and the way into its five sub-pages. */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyState, relativeTime } from '@/shared/components'
import { Chip, Mark, Meter, SearchBox } from '../components'
import { DeleteDialog } from '../components/DeleteDialog'
import { RowMenu } from '../components/RowMenu'
import { NodeTypeDialog } from './NodeTypeDialog'
import { nodeTypesApi } from '../api/brain.api'
import { invalidateModel, useDqCompliance } from '../hooks'
import type { GraphModel, NodeTypeDef } from '../types/brain.types'

export function NodesTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<NodeTypeDef | null>(null)
  const [search, setSearch] = useState('')

  // Compliance is reported per rule; the column is per node type, so the rules
  // belonging to a type are pooled rather than averaged — one rule checked ten
  // thousand times should not be outweighed by one checked twice.
  const compliance = useDqCompliance(graphId)
  const passRate = useMemo(() => {
    const totals: Record<string, { checked: number; passed: number }> = {}
    for (const rule of compliance.data ?? []) {
      const bucket = (totals[rule.node_type_id] ??= { checked: 0, passed: 0 })
      bucket.checked += rule.checked
      bucket.passed += rule.passed
    }
    return totals
  }, [compliance.data])

  const open = (nodeTypeId: string) => navigate(`/brain/${graphId}/nodes/${nodeTypeId}`)

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return model.node_types
    return model.node_types.filter(
      (n) =>
        n.key.toLowerCase().includes(needle) ||
        (n.description ?? '').toLowerCase().includes(needle) ||
        n.properties.some((p) => p.key.toLowerCase().includes(needle)),
    )
  }, [model.node_types, search])

  return (
    <div className="brain-panel">
      <div className="brain-colstack">
        <div className="cp-toolbar">
          <h1>Node types</h1>
          <Chip>
            {model.node_types.length} type{model.node_types.length === 1 ? '' : 's'}
          </Chip>
          <span className="cp-spacer" />
          {model.node_types.length > 0 && (
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Search node types or properties…"
            />
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            + New node type
          </button>
          <div className="sub2">
            The model's entity catalogue. Open one for its schema, quality, matching and
            survivorship.
          </div>
        </div>

        {model.node_types.length === 0 ? (
          <EmptyState
            title="No node types yet"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                Add the first one
              </button>
            }
          >
            A node type is a kind of thing you want to track — an account, a person, a document.
            Add one here, or describe what you need and let Generate draft the whole model.
          </EmptyState>
        ) : (
          <>
            {rows.length === 0 ? (
              <EmptyState title="Nothing matches that search">
                Try a different term, or clear the box.
              </EmptyState>
            ) : (
              <div className="cp-card cp-tablewrap">
                <table className="cp-table">
                  <thead>
                    <tr>
                      <th>Node type</th>
                      <th>Category</th>
                      <th>Records</th>
                      <th>Props</th>
                      <th>Edges</th>
                      <th>Rules</th>
                      <th>DQ compliance</th>
                      <th>Modified</th>
                      <th aria-label="Actions" style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((node) => {
                      const rules =
                        (node.dq_rule_count ?? 0) +
                        (node.match_rule_count ?? 0) +
                        (node.survivorship_rule_count ?? 0)
                      const checked = passRate[node.node_type_id]
                      return (
                        <tr
                          key={node.node_type_id}
                          data-clickable="true"
                          onClick={() => open(node.node_type_id)}
                        >
                          <td>
                            <span className="cp-rowtitle">
                              <Mark name={node.key} id={node.node_type_id} />
                              <span className="truncate">{node.key}</span>
                            </span>
                            {node.description && (
                              <div
                                className="small faint truncate"
                                style={{ maxWidth: 320, marginTop: 2 }}
                              >
                                {node.description}
                              </div>
                            )}
                          </td>
                          <td>
                            <Chip tone={node.category}>{node.category}</Chip>
                          </td>
                          <td className="num">{(node.record_count ?? 0).toLocaleString()}</td>
                          <td className="num">{node.property_count}</td>
                          <td className="num">{node.edge_count}</td>
                          <td>
                            {rules === 0 ? (
                              <Chip tone="warn">none</Chip>
                            ) : (
                              <span className="mono small">
                                {node.dq_rule_count ?? 0} dq · {node.match_rule_count ?? 0} match ·{' '}
                                {node.survivorship_rule_count ?? 0} surv
                              </span>
                            )}
                          </td>
                          <td>
                            {/* Nothing checked yet is not 0% — it is no answer. */}
                            {!checked || checked.checked === 0 ? (
                              <span className="faint small">not checked</span>
                            ) : (
                              <span className="row-tight">
                                <Meter value={checked.passed / checked.checked} />
                                <span className="mono small muted">
                                  {Math.round((checked.passed / checked.checked) * 100)}%
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="muted small">{relativeTime(node.updated_at)}</td>
                          <td>
                            <RowMenu
                              label={`Actions for ${node.key}`}
                              items={[
                                { label: 'Open', onSelect: () => open(node.node_type_id) },
                                {
                                  label: 'View data',
                                  onSelect: () =>
                                    navigate(`/objects/${graphId}?object=${node.node_type_id}`),
                                },
                                {
                                  label: 'Delete node type',
                                  danger: true,
                                  hint: 'and its records',
                                  onSelect: () => setDeleting(node),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {deleting && (
        <DeleteDialog
          title="Delete this node type"
          name={deleting.key}
          what="node type"
          previewPath={`/brain/graphs/${graphId}/node-types/${deleting.node_type_id}/deletion-preview`}
          onConfirm={() => nodeTypesApi.remove(graphId, deleting.node_type_id)}
          onDeleted={() => invalidateModel(graphId)}
          onClose={() => setDeleting(null)}
        />
      )}

      {creating && (
        <NodeTypeDialog
          graphId={graphId}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            open(id)
          }}
        />
      )}
    </div>
  )
}
